import { AlertTriangle, Loader2, X } from 'lucide-react';
import { useId } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

type QuestClaimWarningModalProps = {
  isOpen: boolean;
  isConfirming: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const QuestClaimWarningModal: React.FC<QuestClaimWarningModalProps> = ({
  isOpen,
  isConfirming,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const bodyId = useId();

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[500] flex items-end justify-center bg-black/50 p-3 backdrop-blur-sm md:items-center"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isConfirming) {
          onCancel();
        }
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-gray-200 md:p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-50 text-amber-700 ring-1 ring-amber-200">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isConfirming}
            aria-label={t('COMPONENTS.QUEST_CLAIM_WARNING.CLOSE')}
            className="rounded-lg p-2 text-slate-400 transition hover:bg-gray-100 hover:text-slate-700 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <h2 id={titleId} className="mt-5 font-zillaSlab text-2xl text-slate-900">
          {t('COMPONENTS.QUEST_CLAIM_WARNING.TITLE')}
        </h2>
        <p id={bodyId} className="mt-2 font-manrope text-sm leading-6 text-slate-600">
          {t('COMPONENTS.QUEST_CLAIM_WARNING.BODY')}
        </p>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isConfirming}
            className="rounded-xl px-4 py-3 font-manrope text-xs font-extrabold text-slate-700 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:opacity-50"
          >
            {t('COMPONENTS.QUEST_CLAIM_WARNING.CANCEL')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isConfirming}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-3 font-manrope text-xs font-extrabold text-white transition hover:bg-amber-600 disabled:opacity-60"
          >
            {isConfirming && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('COMPONENTS.QUEST_CLAIM_WARNING.CONFIRM')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuestClaimWarningModal;
